import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { IIdName, IPageablePayload } from '../shared/common.types';
import { ICreateAccount, ICreateAccountRaw } from 'src/app/shared/account/create-account.helper';
import { HttpUtilsService } from 'src/app/shared/http-utils/http-utils.service';
import { ItemResponseWrapper, ListResponseWrapper } from 'src/app/shared/http.types';
import { environment } from 'src/environments/environment';

interface IGetShopsPayload extends IPageablePayload {
  name?: string;
  search?: string;
  whitelabelHash?: string;
}

interface IGetShopsByAdvPayload {
  advertiserId: string;
  withActiveProducts?: boolean;
}

@Injectable({ providedIn: 'root' })
export class PublisherShopHttpService {
  constructor(
    private readonly httpClient: HttpClient,
    private readonly httpUtils: HttpUtilsService,
  ) {}

  public getShops(payload: IGetShopsPayload = { page: 1, pageSize: 10 }): Observable<ICreateAccountRaw[]> {
    return this.httpClient.get<ListResponseWrapper<ICreateAccountRaw>>(`${environment.backendBaseUrl}/shops`, { params: { ...payload } })
      .pipe(
        map(this.httpUtils.listWrapperResponseMapper),
      );
  }

  public getShopsByAdv({ advertiserId, withActiveProducts = true }: IGetShopsByAdvPayload): Observable<IIdName[]> {
    return this.httpClient.get<ListResponseWrapper<IIdName>>(
      `${environment.backendBaseUrl}/shops/advertiser/${advertiserId}`,
      { params: { withActiveProducts } },
    )
      .pipe(
        map(this.httpUtils.listWrapperResponseMapper),
        map((response: IIdName[]) => response.sort((a, b) => a.name.localeCompare(b.name)))
      );
  }

  public getShopIdsByWhiteLabelHash(whiteLabelHash: string): Observable<string[]> {
    return this.httpClient
      .get<ItemResponseWrapper<string[]>>(`${environment.backendBaseUrl}/shops/${whiteLabelHash}/id`)
      .pipe(
        map(this.httpUtils.entityWrapperResponseMapper)
      );
  }

  public getShopsSupportedDisplay(advertiserId: string, { limit = 1000, search = '' }: { limit?: number, search?: string } = {}): Observable<ICreateAccount[]> {
    return this.httpClient.get<ListResponseWrapper<ICreateAccount>>(`${environment.backendBaseUrl}/shops/with-display-slots`,
      { params: { advertiserId, limit, search } }
    )
      .pipe(
        map(this.httpUtils.listWrapperResponseMapper),
      );
  }

  public getShopNameById(shopId: string): Observable<string> {
    return this.httpClient
      .get<ItemResponseWrapper<string>>(`${environment.backendBaseUrl}/shops/${shopId}/name`)
      .pipe(
        map(this.httpUtils.entityWrapperResponseMapper),
      );
  }

  public createShop(value: ICreateAccountRaw): Observable<ICreateAccountRaw> {
    return this.httpClient
      .post<ItemResponseWrapper<ICreateAccountRaw>>(environment.backendBaseUrl + '/shops', value)
      .pipe(
        map(this.httpUtils.entityWrapperResponseMapper),
        catchError(this.httpUtils.simplifyErrorResponse$),
      );
  }

  public uploadLogo(logo: File, name: string): Observable<ItemResponseWrapper<string>> {
    const formData: FormData = new FormData();
    formData.append('file', logo, name);

    return this.httpClient
      .post<ItemResponseWrapper<string>>(environment.backendBaseUrl + '/shops/logo/upload', formData)
      .pipe(
        catchError(this.httpUtils.simplifyErrorResponse$)
      );
  }

}
